import { TRPCClientError } from '@trpc/client';
import { ChevronDownIcon, CopyCheckIcon, CopyIcon } from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { twMerge } from 'tailwind-merge';
import modalStore, { ModalOption } from '@client/stores/modalStore';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import { useCommonNavigate } from '@client/component/CommonLink';
import { Modal } from './Modal';

class ViewStore {
    isDetailOpen = false;
    isCopied = false;

    constructor() {
        makeAutoObservable(this);
    }

    toggleDetail() {
        this.isDetailOpen = !this.isDetailOpen;
    }

    async copyDetails(code: string, path: string) {
        let text = '';
        if (code) {
            text += `코드: ${code}\n`;
        }
        if (path) {
            text += `경로: ${path}\n`;
        }

        await navigator.clipboard.writeText(text);

        runInAction(() => {
            this.isCopied = true;
        });
    }
}

const ErrorModal = observer(({ modalId, option }: { modalId: string; option: ModalOption }) => {
    const viewStore = useMobxInstance(ViewStore);
    const { navigate } = useCommonNavigate();
    const { error, message: messageOption, linkTo } = option.error!;

    let message = messageOption || '';
    let path = '';
    let stack = '';
    let code = '';

    if (error instanceof TRPCClientError) {
        path = error.data?.path;
        stack = error.data?.stack;
        message = error.data?.cause?.message || error.message || message;
        code = error.data?.code;
    } else if (error instanceof Error) {
        message = error.message || message;
    } else {
        message = String(error) || message;
    }

    if (messageOption) {
        message = messageOption;
    }

    if (!message) {
        message = '오류가 발생했습니다. 다시 시도해주세요.';
    }

    const hasDetails = path || code;

    return (
        <Modal.Dialog modalId={modalId}>
            <Modal.BodyScroll>
                <p>{message}</p>
                {hasDetails && (
                    <div className="mt-4">
                        <button
                            type="button"
                            className="text-base-content/60 flex cursor-pointer items-center gap-1 text-xs"
                            onClick={() => {
                                viewStore.toggleDetail();
                            }}
                        >
                            자세히 보기
                        </button>
                        {viewStore.isDetailOpen && (
                            <div className="flex flex-row items-center justify-between">
                                <div className="text-base-content/80 mt-2 space-y-1 text-sm">
                                    {code && (
                                        <div className="flex items-center gap-1">
                                            <span className="text-base-content/70 text-xs">
                                                코드:
                                            </span>{' '}
                                            {code}
                                        </div>
                                    )}
                                    {path && (
                                        <div className="flex items-center gap-1">
                                            <span className="text-base-content/70 text-xs">
                                                경로:
                                            </span>{' '}
                                            {path}
                                        </div>
                                    )}
                                </div>
                                <div className="shrink-0">
                                    <button
                                        className={twMerge('btn btn-circle btn-ghost btn-sm')}
                                        onClick={async () => {
                                            await viewStore.copyDetails(code, path);
                                        }}
                                    >
                                        {viewStore.isCopied ? (
                                            <CopyCheckIcon
                                                className="size-4"
                                                color="var(--color-primary)"
                                            />
                                        ) : (
                                            <CopyIcon className="size-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {import.meta.env.DEV && stack && (
                    <pre className="bg-base-200 mt-4 w-full overflow-auto rounded-md p-2">
                        <code className="text-base-content/65 text-xs">{stack}</code>
                    </pre>
                )}
            </Modal.BodyScroll>
            <Modal.ActionButton
                modalId={modalId}
                confirmStyle="default"
                isShowCancel={true}
                cancelText="닫기"
                confirmText="홈으로 이동"
                onConfirm={() => {
                    navigate(linkTo || '/');
                    modalStore.setReturnValue(modalId, true);
                }}
            />
        </Modal.Dialog>
    );
});

export default ErrorModal;
